import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast, ToastContainer } from "../../components/common/Toast";
import BlogTopicsPanel from "../../components/admin/BlogTopicsPanel";
import {
  blogAdminService,
  BlogArticleListItem,
  BlogConfig,
} from "../../services/blogAdminService";

const fmt = (v: string | null): string =>
  v ? new Date(v).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

const statusBadge = (s: string) => {
  const map: Record<string, string> = {
    published: "bg-green-600/20 text-green-400 border-green-600/50",
    draft: "bg-gray-600/20 text-gray-300 border-gray-600/50",
    archived: "bg-gray-600/20 text-gray-400 border-gray-600/50",
    failed: "bg-red-600/20 text-red-400 border-red-600/50",
    generating: "bg-amber-600/20 text-amber-400 border-amber-600/50",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${map[s] ?? map.draft}`}>
      {s}
    </span>
  );
};

type Tab = "articles" | "topics" | "config";

const AdminBlogPage: React.FC = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, success, error } = useToast();
  const notify = (m: string, t?: "success" | "error") =>
    t === "error" ? error(m) : success(m);

  const [tab, setTab] = useState<Tab>("articles");
  const [articles, setArticles] = useState<BlogArticleListItem[]>([]);
  const [config, setConfig] = useState<BlogConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        blogAdminService.listArticles(),
        blogAdminService.getConfig(),
      ]);
      setArticles(a.articles);
      setConfig(c);
    } catch {
      error("Failed to load blog admin data");
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    void load();
  }, [load]);

  const generateNow = async () => {
    setBusy(true);
    try {
      const r = await blogAdminService.publishNow();
      if (r.published > 0) success(`Published: ${r.slug}`);
      else notify(`Nothing published (${r.skippedReason ?? "no result"})`, "error");
      await load();
    } catch {
      error("Generate-now failed");
    } finally {
      setBusy(false);
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    setBusy(true);
    try {
      const updated = await blogAdminService.updateConfig({
        status: config.status,
        cadence: config.cadence,
        publish_hour_utc: config.publish_hour_utc,
        publish_day_of_week: config.publish_day_of_week,
        topic_batch_size: config.topic_batch_size,
        topic_refill_threshold: config.topic_refill_threshold,
        llm_system_prompt: config.llm_system_prompt,
        generation_model: config.generation_model,
      });
      setConfig(updated);
      success("Config saved");
    } catch {
      error("Failed to save config");
    } finally {
      setBusy(false);
    }
  };

  const tabBtn = (id: Tab, label: string) => (
    <button
      onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm font-semibold rounded-md ${
        tab === id ? "bg-violet-600 text-white" : "text-gray-400 hover:text-white"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-5xl mx-auto">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Blog automation</h1>
          <p className="text-sm text-gray-400">
            AI-generated, SEO-optimized articles published from the topic queue.
            {config && (
              <>
                {" "}Engine is{" "}
                <span className={config.status === "active" ? "text-green-400" : "text-amber-400"}>
                  {config.status}
                </span>.
              </>
            )}
          </p>
        </div>
        <button
          onClick={generateNow}
          disabled={busy}
          className="px-4 py-2 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-50"
        >
          Generate now
        </button>
      </div>

      <div className="flex gap-2 mb-5 border-b border-gray-800 pb-3">
        {tabBtn("articles", "Articles")}
        {tabBtn("topics", "Topics")}
        {tabBtn("config", "Config")}
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : tab === "articles" ? (
        articles.length === 0 ? (
          <p className="text-gray-400 text-sm">No articles yet. Add topics, then Generate now.</p>
        ) : (
          <ul className="divide-y divide-gray-800 border border-gray-800 rounded-md">
            {articles.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-900/50 cursor-pointer"
                onClick={() => navigate(`/admin/blog/${a.id}`)}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{a.title}</p>
                  <p className="text-xs text-gray-500 truncate">
                    /{a.slug} · {a.category} · {fmt(a.pub_date)}
                  </p>
                </div>
                {statusBadge(a.status)}
              </li>
            ))}
          </ul>
        )
      ) : tab === "topics" ? (
        <BlogTopicsPanel notify={notify} />
      ) : config ? (
        <div className="space-y-4 max-w-2xl">
          <div className="grid grid-cols-2 gap-4">
            <label className="text-sm text-gray-300">
              Status
              <select
                value={config.status}
                onChange={(e) => setConfig({ ...config, status: e.target.value })}
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white"
              >
                <option value="inactive">inactive</option>
                <option value="active">active</option>
              </select>
            </label>
            <label className="text-sm text-gray-300">
              Cadence
              <select
                value={config.cadence}
                onChange={(e) => setConfig({ ...config, cadence: e.target.value })}
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white"
              >
                <option value="weekly">weekly</option>
                <option value="daily">daily</option>
                <option value="custom">custom</option>
              </select>
            </label>
            <label className="text-sm text-gray-300">
              Publish hour (UTC)
              <input
                type="number"
                min={0}
                max={23}
                value={config.publish_hour_utc}
                onChange={(e) => setConfig({ ...config, publish_hour_utc: Number(e.target.value) })}
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white"
              />
            </label>
            <label className="text-sm text-gray-300">
              Model override
              <input
                value={config.generation_model ?? ""}
                onChange={(e) => setConfig({ ...config, generation_model: e.target.value || null })}
                placeholder="(default)"
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white"
              />
            </label>
          </div>
          <label className="block text-sm text-gray-300">
            System prompt
            <textarea
              value={config.llm_system_prompt}
              onChange={(e) => setConfig({ ...config, llm_system_prompt: e.target.value })}
              rows={6}
              className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white font-mono text-xs"
            />
          </label>
          <button
            onClick={saveConfig}
            disabled={busy}
            className="px-4 py-2 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-50"
          >
            Save config
          </button>
        </div>
      ) : (
        <p className="text-gray-400 text-sm">No config row found.</p>
      )}
    </div>
  );
};

export default AdminBlogPage;
